import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';
import { ASAAS_LEGAL_NAME } from '@/lib/asaasDisclosure';

export default function PrivacyPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-muted/30">
      <div className="mx-auto max-w-3xl px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
            className="mb-4"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Voltar
          </Button>
          <h1 className="text-2xl font-bold text-foreground">Política de privacidade</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Última atualização: 22 de janeiro de 2025
          </p>
        </div>

        {/* Content */}
        <div className="rounded-xl border border-border bg-card p-6 space-y-6 text-foreground">
          <section className="space-y-3">
            <h2 className="text-lg font-semibold">1. Introdução</h2>
            <p className="text-muted-foreground leading-relaxed">
              Esta Política de Privacidade descreve como o Orçamigo ("nós", "nosso" ou "App") 
              coleta, usa, armazena e protege suas informações pessoais. Estamos comprometidos 
              com a proteção da sua privacidade em conformidade com a Lei Geral de Proteção 
              de Dados (LGPD - Lei nº 13.709/2018), o Regulamento Geral de Proteção de Dados 
              (GDPR) e as políticas do Google Play.
            </p>
            <p className="text-muted-foreground leading-relaxed">
              Ao usar o Orçamigo, você concorda com a coleta e uso de informações conforme 
              descrito nesta política.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold">2. Dados que coletamos</h2>
            
            <div className="space-y-4">
              <div>
                <h3 className="font-medium text-foreground">2.1 Dados de cadastro</h3>
                <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-4 mt-1">
                  <li><strong>E-mail:</strong> Para autenticação e comunicação</li>
                  <li><strong>Senha:</strong> Armazenada de forma criptografada (hash bcrypt)</li>
                  <li><strong>Nome:</strong> Para personalização e identificação</li>
                  <li><strong>Foto de perfil:</strong> Quando você usa login com Google</li>
                </ul>
              </div>

              <div>
                <h3 className="font-medium text-foreground">2.2 Dados de uso do App</h3>
                <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-4 mt-1">
                  <li><strong>Orçamentos:</strong> Documentos que você cria e gerencia</li>
                  <li><strong>Clientes:</strong> Informações de clientes que você cadastra (nome, telefone, e-mail, endereço)</li>
                  <li><strong>Produtos/Serviços:</strong> Itens do seu catálogo</li>
                  <li><strong>Empresas:</strong> Dados das empresas que você configura (nome, logo, CNPJ, contato)</li>
                  <li><strong>Fotos:</strong> Imagens de produtos, logos e documentos que você envia</li>
                </ul>
              </div>

              <div>
                <h3 className="font-medium text-foreground">2.3 Dados técnicos</h3>
                <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-4 mt-1">
                  <li><strong>Endereço IP:</strong> Para segurança e prevenção de fraudes</li>
                  <li><strong>Tipo de dispositivo:</strong> Modelo, sistema operacional e versão</li>
                  <li><strong>Identificadores:</strong> IDs de instalação e tokens de notificação</li>
                  <li><strong>Logs de acesso:</strong> Datas e horários de uso</li>
                </ul>
              </div>

              <div>
                <h3 className="font-medium text-foreground">2.4 Dados de pagamento</h3>
                <p className="text-muted-foreground ml-4 mt-1">
                  Os pagamentos são processados pelo Google Play (Android) ou Stripe (Web). 
                  <strong> Não armazenamos dados de cartão de crédito.</strong> Apenas mantemos 
                  registros de status de assinatura e IDs de transação para gestão do plano.
                </p>
              </div>
            </div>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold">3. Como usamos seus dados</h2>
            <p className="text-muted-foreground leading-relaxed">
              Utilizamos seus dados para:
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-4">
              <li><strong>Fornecer o serviço:</strong> Criar, armazenar e sincronizar seus orçamentos e dados</li>
              <li><strong>Autenticação:</strong> Verificar sua identidade e manter sua sessão segura</li>
              <li><strong>Notificações:</strong> Enviar alertas sobre visualizações e aprovações de orçamentos</li>
              <li><strong>Pagamentos:</strong> Processar assinaturas e gerenciar seu plano</li>
              <li><strong>Suporte:</strong> Responder suas dúvidas e resolver problemas</li>
              <li><strong>Melhorias:</strong> Analisar uso agregado para aprimorar o App</li>
              <li><strong>Segurança:</strong> Detectar e prevenir fraudes ou uso indevido</li>
              <li><strong>Comunicação:</strong> Enviar informações importantes sobre o serviço</li>
              <li><strong>Obrigações legais:</strong> Cumprir exigências legais quando aplicável</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold">4. Base legal para tratamento (LGPD)</h2>
            <p className="text-muted-foreground leading-relaxed">
              Tratamos seus dados com base nas seguintes hipóteses legais:
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-4">
              <li><strong>Consentimento (Art. 7º, I):</strong> Ao criar sua conta e aceitar esta política</li>
              <li><strong>Execução de contrato (Art. 7º, V):</strong> Para fornecer o serviço contratado</li>
              <li><strong>Interesse legítimo (Art. 7º, IX):</strong> Para segurança, prevenção de fraudes e melhorias</li>
              <li><strong>Cumprimento de obrigação legal (Art. 7º, II):</strong> Quando exigido por lei</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold">5. Permissões do dispositivo</h2>
            <p className="text-muted-foreground leading-relaxed">
              O App pode solicitar as seguintes permissões:
            </p>
            
            <div className="space-y-3 ml-4">
              <div className="p-3 bg-muted/50 rounded-lg">
                <p className="font-medium text-foreground">📷 Câmera</p>
                <p className="text-sm text-muted-foreground mt-1">
                  <strong>Finalidade:</strong> Capturar fotos de produtos, logos de empresas e documentos.<br/>
                  <strong>Uso:</strong> As fotos são armazenadas em servidores seguros e associadas aos seus orçamentos.
                </p>
              </div>
              
              <div className="p-3 bg-muted/50 rounded-lg">
                <p className="font-medium text-foreground">📁 Armazenamento/galeria</p>
                <p className="text-sm text-muted-foreground mt-1">
                  <strong>Finalidade:</strong> Selecionar imagens da galeria e salvar PDFs gerados.<br/>
                  <strong>Uso:</strong> Acesso apenas quando você escolhe enviar uma imagem ou baixar um documento.
                </p>
              </div>
              
              <div className="p-3 bg-muted/50 rounded-lg">
                <p className="font-medium text-foreground">👥 Contatos</p>
                <p className="text-sm text-muted-foreground mt-1">
                  <strong>Finalidade:</strong> Salvar informações de clientes diretamente nos contatos do dispositivo.<br/>
                  <strong>Uso:</strong> Opcional. Usado apenas quando você clica em "Salvar nos contatos". Não lemos ou sincronizamos seus contatos existentes.
                </p>
              </div>
              
              <div className="p-3 bg-muted/50 rounded-lg">
                <p className="font-medium text-foreground">🔔 Notificações</p>
                <p className="text-sm text-muted-foreground mt-1">
                  <strong>Finalidade:</strong> Enviar alertas sobre visualizações e aprovações de orçamentos.<br/>
                  <strong>Uso:</strong> Você pode desativar a qualquer momento nas configurações do dispositivo.
                </p>
              </div>
            </div>

            <p className="text-muted-foreground leading-relaxed mt-3">
              Todas as permissões são opcionais. O App funciona sem elas, porém algumas 
              funcionalidades ficarão indisponíveis.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold">6. Compartilhamento de dados</h2>
            <p className="text-muted-foreground leading-relaxed">
              Seus dados podem ser compartilhados com:
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
              <li>
                <strong>Supabase (infraestrutura):</strong> Banco de dados, autenticação e armazenamento de arquivos. 
                Servidores seguros com criptografia.
              </li>
              <li>
                <strong>Google Play / RevenueCat:</strong> Processamento de assinaturas no Android.
              </li>
              <li>
                <strong>Stripe:</strong> Processamento de pagamentos na versão web.
              </li>
              {/*
                A razão social sai de `ASAAS_LEGAL_NAME` (e não digitada aqui)
                para não divergir da cláusula dos Termos de Uso, que é o texto
                do modelo do Asaas e não pode ser reescrito. Ver
                `src/lib/asaasDisclosure.ts`.
              */}
              <li>
                <strong>Asaas (serviços financeiros):</strong> Quando você usa a conta de
                pagamento e as cobranças Pix do app, seus dados de cadastro e os dados de quem
                paga (nome e CPF/CNPJ) são tratados pelo {ASAAS_LEGAL_NAME}, instituição de
                pagamento autorizada a funcionar pelo Banco Central do Brasil e responsável por
                essas operações.
              </li>
              <li>
                <strong>OneSignal:</strong> Envio de notificações push. Recebe apenas IDs anônimos.
              </li>
              <li>
                <strong>Google (OAuth):</strong> Quando você usa login com Google. Recebemos apenas 
                nome, e-mail e foto de perfil.
              </li>
              <li>
                <strong>Autoridades competentes:</strong> Quando exigido por ordem judicial ou lei.
              </li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-3 font-medium">
              ⚠️ Não vendemos, alugamos ou comercializamos seus dados pessoais com terceiros 
              para fins de marketing ou publicidade.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold">7. Links públicos de orçamentos</h2>
            <p className="text-muted-foreground leading-relaxed">
              Quando você compartilha um orçamento via link público:
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-4">
              <li>Qualquer pessoa com o link pode visualizar o orçamento</li>
              <li>São exibidos: itens, valores, dados da sua empresa e nome do cliente</li>
              <li>Dados sensíveis como telefone e endereço do cliente são ocultados</li>
              <li>Você pode desativar o link a qualquer momento</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold">8. Seus direitos (LGPD/GDPR)</h2>
            <p className="text-muted-foreground leading-relaxed">
              Você tem os seguintes direitos sobre seus dados:
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-4">
              <li><strong>Acesso:</strong> Solicitar cópia de todos os seus dados pessoais</li>
              <li><strong>Correção:</strong> Atualizar dados incorretos ou incompletos</li>
              <li><strong>Exclusão:</strong> Solicitar a remoção dos seus dados ("direito ao esquecimento")</li>
              <li><strong>Portabilidade:</strong> Receber seus dados em formato estruturado (JSON/CSV)</li>
              <li><strong>Revogação:</strong> Retirar o consentimento a qualquer momento</li>
              <li><strong>Oposição:</strong> Opor-se a determinados tratamentos de dados</li>
              <li><strong>Informação:</strong> Saber com quem seus dados foram compartilhados</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-2">
              Para exercer seus direitos, acesse as opções de "Excluir minha conta" ou 
              "Limpar meus dados" nas Configurações do App, ou entre em contato pelo e-mail 
              abaixo.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold">9. Segurança dos dados</h2>
            <p className="text-muted-foreground leading-relaxed">
              Implementamos medidas técnicas e organizacionais para proteger seus dados:
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-4">
              <li><strong>Criptografia em trânsito:</strong> HTTPS/TLS em todas as comunicações</li>
              <li><strong>Criptografia em repouso:</strong> Dados armazenados em servidores criptografados</li>
              <li><strong>Senhas:</strong> Hash bcrypt, nunca armazenadas em texto</li>
              <li><strong>Controle de acesso:</strong> Row Level Security (RLS) no banco de dados</li>
              <li><strong>Tokens seguros:</strong> Autenticação via tokens JWT com expiração</li>
              <li><strong>Backups:</strong> Cópias de segurança regulares com recuperação de desastres</li>
              <li><strong>Monitoramento:</strong> Detecção de atividades suspeitas</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold">10. Retenção de dados</h2>
            <p className="text-muted-foreground leading-relaxed">
              Mantemos seus dados conforme as seguintes regras:
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-4">
              <li><strong>Conta ativa:</strong> Dados mantidos enquanto você usar o serviço</li>
              <li><strong>Após exclusão de dados:</strong> Removidos em até 30 dias</li>
              <li><strong>Após exclusão de conta:</strong> Todos os dados removidos em até 30 dias</li>
              <li><strong>Backups:</strong> Podem conter dados por até 90 dias após exclusão</li>
              <li><strong>Dados de pagamento:</strong> Mantidos conforme obrigações fiscais (5 anos)</li>
              <li><strong>Logs de segurança:</strong> Mantidos por 6 meses para auditoria</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold">11. Cookies e tecnologias similares</h2>
            <p className="text-muted-foreground leading-relaxed">
              O Orçamigo utiliza:
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-4">
              <li><strong>Cookies de sessão:</strong> Para manter você logado (essencial)</li>
              <li><strong>LocalStorage:</strong> Para preferências e rascunhos locais</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-2">
              <strong>Não utilizamos</strong> cookies de rastreamento, publicidade ou analytics 
              de terceiros que coletam dados pessoais.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold">12. Transferência internacional</h2>
            <p className="text-muted-foreground leading-relaxed">
              Nossos servidores estão localizados nos Estados Unidos (Supabase/AWS). Seus 
              dados são transferidos com as seguintes salvaguardas:
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-4">
              <li>Cláusulas contratuais padrão aprovadas</li>
              <li>Criptografia de ponta a ponta</li>
              <li>Conformidade com LGPD e GDPR pelos provedores</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold">13. Menores de idade</h2>
            <p className="text-muted-foreground leading-relaxed">
              O Orçamigo é destinado a usuários com 18 anos ou mais. Não coletamos 
              intencionalmente dados de menores de idade. Se tomarmos conhecimento de que 
              coletamos dados de um menor, excluiremos imediatamente.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold">14. Alterações nesta política</h2>
            <p className="text-muted-foreground leading-relaxed">
              Podemos atualizar esta Política de Privacidade periodicamente. Alterações 
              significativas serão notificadas através do App ou por e-mail com pelo menos 
              30 dias de antecedência. A data de "última atualização" no topo sempre reflete 
              a versão mais recente.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold">15. Contato e DPO</h2>
            <p className="text-muted-foreground leading-relaxed">
              Para exercer seus direitos, esclarecer dúvidas ou fazer reclamações sobre o 
              tratamento de dados, entre em contato com nosso Encarregado de Proteção de 
              Dados (DPO):
            </p>
            <ul className="list-none text-muted-foreground space-y-1 ml-4">
              <li><strong>E-mail do DPO:</strong> privacidade@orcamigo.com.br</li>
              <li><strong>E-mail geral:</strong> contato@orcamigo.com.br</li>
              <li><strong>Suporte:</strong> suporte@orcamigo.com.br</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-3">
              Você também tem o direito de apresentar reclamação à Autoridade Nacional de 
              Proteção de Dados (ANPD) se considerar que seus direitos não foram atendidos.
            </p>
          </section>

          <section className="space-y-3 border-t border-border pt-6">
            <h2 className="text-lg font-semibold">Resumo de práticas de dados</h2>
            <div className="grid gap-3">
              <div className="flex items-start gap-3 p-3 bg-primary/10 rounded-lg">
                <span className="text-primary">✓</span>
                <p className="text-sm text-muted-foreground">
                  <strong>Coletamos apenas o necessário</strong> para fornecer o serviço
                </p>
              </div>
              <div className="flex items-start gap-3 p-3 bg-primary/10 rounded-lg">
                <span className="text-primary">✓</span>
                <p className="text-sm text-muted-foreground">
                  <strong>Não vendemos seus dados</strong> para terceiros
                </p>
              </div>
              <div className="flex items-start gap-3 p-3 bg-primary/10 rounded-lg">
                <span className="text-primary">✓</span>
                <p className="text-sm text-muted-foreground">
                  <strong>Você controla seus dados</strong> e pode excluí-los a qualquer momento
                </p>
              </div>
              <div className="flex items-start gap-3 p-3 bg-primary/10 rounded-lg">
                <span className="text-primary">✓</span>
                <p className="text-sm text-muted-foreground">
                  <strong>Usamos criptografia</strong> para proteger suas informações
                </p>
              </div>
              <div className="flex items-start gap-3 p-3 bg-primary/10 rounded-lg">
                <span className="text-primary">✓</span>
                <p className="text-sm text-muted-foreground">
                  <strong>Permissões são opcionais</strong> e podem ser revogadas
                </p>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
